import React, {
  useEffect,
  useImperativeHandle,
  useState,
  forwardRef,
} from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  RowData,
  Row,
  Column,
  sortingFns,
  VisibilityState,
  ColumnOrderState,
  ColumnPinningState,
  ColumnFiltersState,
  PaginationState,
  RowSelectionState,
  OnChangeFn,
} from '@tanstack/react-table';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { dateRangeFilterFn } from '@/components/ui/tableFilterFns';
import { downloadCsv, rowsToCsv } from '@/components/ui/csvExport';

// ---------------------------------------------------------------------------
// Column meta extension
//
// overflow — controls how cell content behaves when it doesn't fit:
//   'truncate' (default) — wraps content in <div className="truncate" title={value}>.
//   'nowrap'             — adds whitespace-nowrap on the <td>. Use for dates, IDs,
//                          and status badges that must never wrap.
//   'wrap'               — no constraint; content wraps naturally.
//
// minWidth / maxWidth / width — optional width hints. How they're applied
// depends on whether the table uses column pinning (i.e. the `columnPinning`
// prop is supplied):
//   - Without pinning (most tables): applied as plain inline style on <th>/<td>
//     under the browser's normal auto table layout — minWidth is a floor,
//     maxWidth is a cap, width can be a fixed number OR a string like '100%'
//     to make a column greedy (absorb remaining space).
//   - With pinning (Sites, Studies): pinned columns need a definite pixel
//     width for their sticky offsets (getStart('left')/getAfter('right')) to
//     be correct, so every column instead gets a derived numeric TanStack
//     `size` (see "Column sizing" below) and the table renders with
//     `table-layout: fixed`. A string `width` is ignored here (TanStack sizes
//     are numeric) and falls back to minWidth/maxWidth/150.
//
// exportable — set false to exclude a column from CSV export (e.g. an action
//   column like "view" with no exportable data). Defaults to true.
//
// csvFormatter — customizes a column's exported string value. Receives the
//   column's raw accessor value plus the row's original data (for columns
//   whose display combines multiple fields, e.g. a label + a badge). Falls
//   back to `String(value ?? '')` when omitted.
// ---------------------------------------------------------------------------
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    overflow?: 'truncate' | 'nowrap' | 'wrap';
    minWidth?: number;
    maxWidth?: number;
    width?: number | string;
    exportable?: boolean;
    csvFormatter?: (value: unknown, row: TData) => string;
  }
}

// Column sizing (pinning tables only) — every column gets a definite pixel
// width (not resizable by the user; that was tried and felt inconsistent, so
// it was pulled). This is what `table-layout: fixed` needs to render, and
// what pinned columns need to compute correct sticky offsets from — see
// getStart('left')/getAfter('right') usage further down — which is what
// actually stops long content from overlapping its neighbor. Scoped to
// pinning tables only: forcing single fixed-pixel sizes onto every column
// breaks the flexible auto-layout sizing (min/max ranges, greedy '100%'
// columns) that non-pinning tables like DocumentsTable rely on.
// A column's width comes from its own ColumnDef.size if set, else from
// meta.width/maxWidth/minWidth, else a 150px default.
const DEFAULT_SIZE = 150;

function withDerivedSizing<TData extends { id?: string }>(
  columns: ColumnDef<TData, unknown>[]
): ColumnDef<TData, unknown>[] {
  return columns.map(column => {
    const meta = column.meta;
    const metaWidth = typeof meta?.width === 'number' ? meta.width : undefined;
    const size =
      column.size ??
      metaWidth ??
      meta?.maxWidth ??
      meta?.minWidth ??
      DEFAULT_SIZE;
    return { ...column, size };
  });
}

// A sorting function that places null/undefined values last regardless of direction.
const nullableDateSort = <TData extends RowData>(
  rowA: Row<TData>,
  rowB: Row<TData>,
  columnId: string
): number => {
  const a = rowA.getValue(columnId) as string | null | undefined;
  const b = rowB.getValue(columnId) as string | null | undefined;
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return sortingFns.alphanumeric(rowA, rowB, columnId);
};
nullableDateSort.autoRemove = (val: unknown) => !val;

interface DataTableProps<TData extends { id?: string }> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  /**
   * Fires on click and on Enter/Space when the row has keyboard focus, so the
   * event is a mouse OR keyboard event. Handlers that branch on mouse-only
   * properties must guard for that — `event.button` is undefined for a
   * keyboard event, and an unguarded `!== 0` check silently swallows it.
   */
  onRowClick?: (
    row: TData,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void;
  emptyMessage?: string;
  className?: string;
  highlightId?: string;
  highlightIdPrefix?: string;
  /**
   * Column visibility/order/pinning/filters are opt-in controlled state — pass
   * a value + its `on*Change` handler together (e.g. from `useColumnPreferences`)
   * to enable that feature. Omitted pairs leave TanStack's defaults in place, so
   * existing callers are unaffected.
   */
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
  columnPinning?: ColumnPinningState;
  onColumnPinningChange?: OnChangeFn<ColumnPinningState>;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /**
   * Pagination — opt-in and always controlled by the caller (like
   * `columnFilters`/`columnVisibility` above), since most existing tables
   * (embedded/scoped lists) intentionally show every row and the few that
   * paginate need to reset `pageIndex` themselves whenever a filter/sort
   * change narrows the result set (DataTable has no way to know that on its
   * own). Omit entirely to render every row, unpaginated.
   *
   * Without `server`: client-side pagination — DataTable paginates the full
   * `data` array itself (via TanStack's row model), so `data` should hold
   * every filtered + sorted row.
   *
   * With `server`: `data` holds only the current page (fetched by the caller
   * for `pageIndex`/`pageSize`), so TanStack does no client-side slicing — it
   * just renders `data` as-is. `pageCount`/`rowCount` come from the server's
   * total-row count (`rowCount` drives the "Showing X–Y of Z" footer, which
   * `data.length` alone can't — that's just the current page). Column
   * sorting is also forced manual while this is set (see
   * `sorting`/`onSortingChange`) since the caller's fetch, not TanStack, is
   * responsible for ordering the (partial) row set.
   */
  pagination?: {
    pageIndex: number;
    pageSize: number;
    onPaginationChange: OnChangeFn<PaginationState>;
    server?: { pageCount: number; rowCount: number };
  };
  /**
   * Controlled sort state — optional, mirrors the `columnFilters` pattern
   * above. Omit to keep the component's own internal sort state (every
   * existing caller). Provide both when a caller needs to read the current
   * sort (e.g. to drive a server request) or preserve it across a mode
   * switch; required when `pagination.server` is set.
   */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** Fires whenever the post-filter, pre-pagination row count changes — the
   * count `exportCsv` would actually write. Use it to disable an "Export"
   * trigger when active filters exclude every row. */
  onExportRowCountChange?: (count: number) => void;
  /**
   * Row selection — opt-in controlled state, like `columnFilters` above. Pass
   * the value and its handler together to enable it; omit both and the table
   * renders no selection state at all (every existing caller).
   *
   * Keys are row ids, not row indexes, because `getRowId` reads `row.id` (see
   * `useReactTable` below). Filtering, sorting and pagination all reorder the
   * row array, and index-keyed selection would then point at whichever row
   * happens to sit at that position afterwards — i.e. silently select
   * something the user never picked. Callers therefore get back the same ids
   * they can hand straight to an API.
   *
   * `enableRowSelection` gates which rows may be selected; a predicate also
   * excludes those rows from the header select-all, so "select all" cannot
   * produce a selection the row's own checkbox refuses.
   */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
}

export interface DataTableHandle {
  /** Exports the currently visible columns (in the user's order) for every
   * filtered + sorted row — not just the current page — as a downloaded CSV. */
  exportCsv: (filename?: string) => void;
}

function DataTableInner<TData extends { id?: string }>(
  {
    columns,
    data,
    onRowClick,
    emptyMessage = 'No results.',
    className,
    highlightId,
    highlightIdPrefix = '',
    columnVisibility,
    onColumnVisibilityChange,
    columnOrder,
    onColumnOrderChange,
    columnPinning,
    onColumnPinningChange,
    columnFilters,
    onColumnFiltersChange,
    pagination,
    sorting: controlledSorting,
    onSortingChange,
    onExportRowCountChange,
    rowSelection,
    onRowSelectionChange,
    enableRowSelection,
  }: DataTableProps<TData>,
  ref: React.ForwardedRef<DataTableHandle>
) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const sorting = controlledSorting ?? internalSorting;
  const isServerPaginated = Boolean(pagination?.server);

  // Only pinning tables need every column forced to a definite pixel size
  // (see the "Column sizing" comment above) — other tables keep their
  // columns' original ColumnDef so auto-layout sizing behaves as before.
  const usesPinning = columnPinning !== undefined;

  const sizedColumns = React.useMemo(
    () => (usesPinning ? withDerivedSizing(columns) : columns),
    [columns, usesPinning]
  );

  // The definite pixel width the table needs when every column has a fixed
  // size (pinning tables only) — see the `tableWidth` comment further down
  // for why this has to be a definite `width`, not just a `min-width`.
  const totalColumnWidth = React.useMemo(
    () =>
      usesPinning
        ? sizedColumns.reduce(
            (sum, column) =>
              sum +
              (typeof column.size === 'number' ? column.size : DEFAULT_SIZE),
            0
          )
        : 0,
    [sizedColumns, usesPinning]
  );

  const table = useReactTable({
    data,
    columns: sizedColumns,
    state: {
      sorting,
      ...(columnVisibility !== undefined && { columnVisibility }),
      ...(columnOrder !== undefined && { columnOrder }),
      ...(columnPinning !== undefined && { columnPinning }),
      ...(columnFilters !== undefined && { columnFilters }),
      ...(pagination && {
        pagination: {
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        },
      }),
      ...(rowSelection !== undefined && { rowSelection }),
    },
    // Selection is keyed on the row's own id rather than its index — see the
    // `rowSelection` prop docs. Falls back to the index only for data that
    // has no id, which no selecting caller does.
    getRowId: (row, index) => row.id ?? String(index),
    onRowSelectionChange,
    enableRowSelection,
    onSortingChange: onSortingChange ?? setInternalSorting,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onColumnPinningChange,
    onColumnFiltersChange,
    onPaginationChange: pagination?.onPaginationChange,
    manualSorting: isServerPaginated,
    manualPagination: isServerPaginated,
    pageCount: pagination?.server?.pageCount,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel:
      pagination && !isServerPaginated ? getPaginationRowModel() : undefined,
    sortingFns: {
      nullableDateSort,
    },
    filterFns: {
      dateRange: dateRangeFilterFn,
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      exportCsv: (filename = 'export.csv') => {
        const exportableColumns = table
          .getVisibleLeafColumns()
          .filter(
            column => column.columnDef.meta?.exportable !== false
          ) as Column<TData, unknown>[];
        const headers = exportableColumns.map(column => {
          const header = column.columnDef.header;
          return typeof header === 'string' ? header : column.id;
        });
        // Sorted row model is the full filtered + sorted set — pagination
        // only slices it for display, so export isn't limited to the current page.
        const rows = table.getSortedRowModel().rows.map(row =>
          exportableColumns.map(column => {
            const value = row.getValue(column.id);
            const formatter = column.columnDef.meta?.csvFormatter;
            return formatter
              ? formatter(value, row.original)
              : String(value ?? '');
          })
        );
        downloadCsv(rowsToCsv(headers, rows), filename);
      },
    }),
    [table]
  );

  // Reports the post-filter row count (what exportCsv would actually write)
  // so a caller can disable an "Export" trigger when filters exclude
  // everything — checking the raw, unfiltered `data` prop for this would
  // leave the trigger enabled while producing a headers-only CSV. Under
  // server pagination, `data` is only the current page, so the sorted row
  // model's length is not the true post-filter count — use the server's
  // total instead (a caller relying on this to disable "Export all" must
  // not see it go to 0 just because the current page happens to be empty).
  const exportRowCount = isServerPaginated
    ? (pagination?.server?.rowCount ?? 0)
    : table.getSortedRowModel().rows.length;
  useEffect(() => {
    onExportRowCountChange?.(exportRowCount);
  }, [exportRowCount, onExportRowCountChange]);

  const totalRows = isServerPaginated
    ? (pagination?.server?.rowCount ?? 0)
    : table.getFilteredRowModel().rows.length;
  const { pageIndex, pageSize: currentPageSize } = table.getState().pagination;
  const startRow = totalRows === 0 ? 0 : pageIndex * currentPageSize + 1;
  const endRow = Math.min((pageIndex + 1) * currentPageSize, totalRows);

  return (
    <div className={className}>
      <div className="overflow-x-auto">
        <table
          className={cn('text-sm', usesPinning ? 'table-fixed' : 'w-full')}
          style={
            // table-layout: fixed only honors each cell's declared width as a
            // ratio for distributing space when the <table> itself has a
            // definite `width` — a `min-width` alone lets the browser fall
            // back to content-based sizing (e.g. long, unbreakable text)
            // for whichever column doesn't get enough of the redistributed
            // space, ballooning it past its declared size and defeating
            // truncation. `max(100%, <sum of column sizes>px)` keeps both
            // properties the old `min-w-full` was chosen for: it fills the
            // container on screens with room to spare (like `width: 100%`
            // would), while still growing past the container — for the
            // wrapper's `overflow-x-auto` to scroll — on a narrow viewport
            // where the columns' natural sum is wider than the container
            // (which a plain `width: 100%` would instead have shrunk).
            usesPinning
              ? { width: `max(100%, ${totalColumnWidth}px)` }
              : undefined
          }
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const thStyle: React.CSSProperties = {};
                  if (usesPinning) {
                    thStyle.width = header.getSize();
                  } else {
                    const { minWidth, maxWidth, width } =
                      header.column.columnDef.meta ?? {};
                    if (minWidth !== undefined) thStyle.minWidth = minWidth;
                    if (maxWidth !== undefined) thStyle.maxWidth = maxWidth;
                    if (width !== undefined) thStyle.width = width;
                  }

                  const pinned = header.column.getIsPinned();
                  if (pinned === 'left') {
                    thStyle.position = 'sticky';
                    thStyle.left = header.column.getStart('left');
                    thStyle.zIndex = 2;
                  } else if (pinned === 'right') {
                    thStyle.position = 'sticky';
                    thStyle.right = header.column.getAfter('right');
                    thStyle.zIndex = 2;
                  }

                  return (
                    <th
                      key={header.id}
                      style={thStyle}
                      className="h-11 px-4 text-left align-middle text-sm font-medium text-gray-600 whitespace-nowrap overflow-hidden bg-gray-50/60 border-b border-gray-200"
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          className="flex w-full items-center gap-1.5 select-none hover:text-gray-800 transition-colors group"
                          onClick={header.column.getToggleSortingHandler()}
                          type="button"
                        >
                          <span className="min-w-0 truncate">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </span>
                          {sorted === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-gray-600 shrink-0" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className="w-3 h-3 text-gray-600 shrink-0" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-gray-300 group-hover:text-gray-500 transition-colors shrink-0" />
                          )}
                        </button>
                      ) : (
                        <span className="block truncate">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-100">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={table.getVisibleLeafColumns().length}
                  className="px-4 py-10 text-center text-sm text-gray-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map(row => {
                const isClickable = Boolean(onRowClick);
                const isHighlighted =
                  highlightId !== undefined && row.original.id === highlightId;
                // A clickable row is the primary way into a record on every list
                // page, so it must be reachable without a mouse — the per-row
                // button only opens the secondary detail drawer.
                const handleRowKeyDown = onRowClick
                  ? (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      // Let controls inside the row (details button, links)
                      // handle their own activation.
                      if (e.target !== e.currentTarget) return;
                      e.preventDefault();
                      onRowClick(row.original, e);
                    }
                  : undefined;

                return (
                  <tr
                    key={row.id}
                    id={
                      isHighlighted
                        ? `${highlightIdPrefix}${highlightId}`
                        : undefined
                    }
                    className={cn(
                      'group transition-colors',
                      isClickable &&
                        'cursor-pointer hover:bg-gray-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                      isHighlighted && 'bg-primary/10'
                    )}
                    tabIndex={isClickable ? 0 : undefined}
                    role={isClickable ? 'button' : undefined}
                    onClick={
                      onRowClick ? e => onRowClick(row.original, e) : undefined
                    }
                    onKeyDown={handleRowKeyDown}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => {
                      const { overflow } = cell.column.columnDef.meta ?? {};
                      const effectiveOverflow = overflow ?? 'truncate';
                      const rendered = flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      );
                      const tdStyle: React.CSSProperties = {};
                      if (usesPinning) {
                        tdStyle.width = cell.column.getSize();
                      } else {
                        const { minWidth, maxWidth, width } =
                          cell.column.columnDef.meta ?? {};
                        if (minWidth !== undefined) tdStyle.minWidth = minWidth;
                        if (maxWidth !== undefined) tdStyle.maxWidth = maxWidth;
                        if (width !== undefined) tdStyle.width = width;
                      }

                      const pinned = cell.column.getIsPinned();
                      if (pinned === 'left') {
                        tdStyle.position = 'sticky';
                        tdStyle.left = cell.column.getStart('left');
                        tdStyle.zIndex = 1;
                      } else if (pinned === 'right') {
                        tdStyle.position = 'sticky';
                        tdStyle.right = cell.column.getAfter('right');
                        tdStyle.zIndex = 1;
                      }

                      return (
                        <td
                          key={cell.id}
                          style={tdStyle}
                          className={cn(
                            'px-4 py-5 align-middle text-gray-700 overflow-hidden',
                            pinned
                              ? isHighlighted
                                ? 'bg-primary/10'
                                : cn(
                                    'bg-white',
                                    isClickable && 'group-hover:bg-gray-50/80'
                                  )
                              : isHighlighted && 'bg-primary/10',
                            isHighlighted &&
                              cellIndex === 0 &&
                              'border-l-2 border-primary',
                            // max-w-0 is the CSS trick that makes truncate work
                            // inside a table cell under auto layout: it gives the
                            // cell a definite max-width so overflow:hidden fires,
                            // while the browser still allocates space from the
                            // table's auto layout algorithm. Pinning tables don't
                            // need it — they already get a definite width from
                            // table-fixed + the derived `size` above.
                            !usesPinning &&
                              effectiveOverflow === 'truncate' &&
                              'max-w-0',
                            effectiveOverflow === 'nowrap' &&
                              'whitespace-nowrap'
                          )}
                        >
                          {effectiveOverflow === 'truncate' ? (
                            <div
                              className="truncate"
                              title={
                                typeof cell.getValue() === 'string'
                                  ? (cell.getValue() as string)
                                  : undefined
                              }
                            >
                              {rendered}
                            </div>
                          ) : (
                            rendered
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {pagination && totalRows > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-600">
          <span>
            Showing {startRow}–{endRow} of {totalRows}
          </span>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>
              Page {pageIndex + 1} of {Math.max(table.getPageCount(), 1)}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export const DataTable = forwardRef(DataTableInner) as <
  TData extends { id?: string },
>(
  props: DataTableProps<TData> & { ref?: React.ForwardedRef<DataTableHandle> }
) => ReturnType<typeof DataTableInner>;
